import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { chatService, feedbackService, folderService, llmConfigService } from '../../services/api';
import ChatHistorySidebar from './ChatHistorySidebar';
import CrossProjectFiltersModal from '../CrossProjectFiltersModal';
import FeedbackModal from '../FeedbackModal';
import ThoughtChain from '../ThoughtChain';
import useConsoleErrors from '../../hooks/useConsoleErrors';
import { useAuth } from '../../context/AuthContext';

const CrossProjectTab = () => {
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [streamingStatus, setStreamingStatus] = useState(null);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [exportingMessageId, setExportingMessageId] = useState(null);
  const [exportedDocument, setExportedDocument] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showGoogleDocsPermissionModal, setShowGoogleDocsPermissionModal] = useState(false);
  const [googleDocsAuthUrl, setGoogleDocsAuthUrl] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackTarget, setFeedbackTarget] = useState({ messageId: null, rating: null, initialType: 'BUG' });
  const [ratedMessages, setRatedMessages] = useState({});
  
  // Model settings
  const [availableModels, setAvailableModels] = useState([]);
  const [sliderPresets, setSliderPresets] = useState([]);
  const [currentModel, setCurrentModel] = useState(null);
  const [currentThinkingLevel, setCurrentThinkingLevel] = useState('low');
  const [sliderValue, setSliderValue] = useState(2);
  const [isCustomSelection, setIsCustomSelection] = useState(false);
  
  // Filter states
  const [allProjects, setAllProjects] = useState([]);
  const [folderTree, setFolderTree] = useState({ shared: [], personal: [], orphanedShared: [] });
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  
  const chatContainerRef = useRef(null);
  const activeStreamControllerRef = useRef(null);
  const { getRecentErrors } = useConsoleErrors();
  const { user } = useAuth();

  const isAbortLikeError = (error) => {
    if (!error) {
      return false;
    }

    return error.name === 'AbortError'
      || error.code === 'REQUEST_ABORTED'
      || error.code === 'ERR_CANCELED';
  };

  const stopActiveResponse = () => {
    if (activeStreamControllerRef.current && !activeStreamControllerRef.current.signal.aborted) {
      activeStreamControllerRef.current.abort();
    }
  };

  const normalizeThinkingLevel = (value) => {
    if (!value || typeof value !== 'string') {
      return 'low';
    }
    const normalized = value.toLowerCase();
    return ['minimal', 'low', 'medium', 'high'].includes(normalized) ? normalized : 'low';
  };

  const buildDefaultSliderPresets = (models) => {
    const firstModel = models[0]?.modelName || null;
    const minimalModel = models.find((model) => model.modelName === 'gemini-3-flash-preview')?.modelName;

    return [1, 2, 3].map((position) => ({
      sliderPosition: position,
      modelName: position === 1 && minimalModel ? minimalModel : firstModel,
      thinkingLevel: position === 1 && minimalModel ? 'minimal' : 'low',
      isActive: true
    }));
  };

  const mergeSliderPresets = (models, presets) => {
    const defaults = buildDefaultSliderPresets(models);
    if (!presets || presets.length === 0) {
      return defaults;
    }

    const byPosition = new Map(presets.map((preset) => [preset.sliderPosition, preset]));
    return defaults.map((fallbackPreset) => {
      const existing = byPosition.get(fallbackPreset.sliderPosition);
      if (!existing) {
        return fallbackPreset;
      }
      return {
        ...existing,
        thinkingLevel: normalizeThinkingLevel(existing.thinkingLevel),
        modelName: existing.modelName || fallbackPreset.modelName,
        isActive: existing.isActive !== false
      };
    });
  };

  const isMinimalSupported = (modelName) => modelName === 'gemini-3-flash-preview';

  const buildModelSelection = (models, modelName) => {
    if (!models || models.length === 0) {
      return null;
    }
    const selected = models.find((model) => model.modelName === modelName) || models[0];
    return {
      modelName: selected.modelName,
      displayName: selected.displayName,
      provider: selected.provider
    };
  };

  const applySliderPreset = (nextSliderValue, presets, models) => {
    const preset = presets.find((entry) => entry.sliderPosition === nextSliderValue && entry.isActive !== false)
      || presets.find((entry) => entry.sliderPosition === nextSliderValue)
      || presets[0];
    if (!preset) {
      return;
    }

    const normalizedThinking = normalizeThinkingLevel(preset.thinkingLevel);
    const safeThinking = normalizedThinking === 'minimal' && !isMinimalSupported(preset.modelName)
      ? 'low'
      : normalizedThinking;

    const selectedModel = buildModelSelection(models, preset.modelName);
    setSliderValue(nextSliderValue);
    setIsCustomSelection(false);
    setCurrentModel(selectedModel);
    setCurrentThinkingLevel(safeThinking);
  };

  const toolLabelMap = {
    analysis_phase_preparation: 'Preparation',
    analysis_phase_review: 'Independent code reviews',
    analysis_phase_consolidation: 'Consolidation and verification',
    analysis_conversation: 'Preparing conversation',
    analysis_load_files: 'Loading files for analysis',
    analysis_select_mode: 'Selecting analysis mode',
    analysis_load_context: 'Loading process context',
    analysis_select_model: 'Selecting model',
    analysis_select_models: 'Selecting reviewer models',
    analysis_record_request: 'Recording request',
    analysis_build_context: 'Preparing code context',
    analysis_load_prompt: 'Loading analysis instructions',
    analysis_prompt_cache: 'Checking prompt cache',
    analysis_single_pass: 'Running code analysis',
    analysis_reviewer_a: 'Reviewer A analysis',
    analysis_reviewer_b: 'Reviewer B analysis',
    analysis_create_cache: 'Creating shared reviewer cache',
    analysis_consolidation_cache: 'Creating consolidation cache',
    analysis_persist_response: 'Saving analysis response',
    retrieve_uipath_code: 'Searching UiPath code',
    retrieve_all_project_code: 'Reviewing full project code',
    retrieve_specific_workflow: 'Retrieving workflow',
    get_project_file_list: 'Listing project files',
    retrieve_activities_by_keyword: 'Searching activities',
    query_uipath_code: 'Querying code',
    knowledge_search: 'Searching code knowledge',
    code_search: 'Searching code',
    search_code: 'Searching code',
    query_uipath_code_sql: 'Querying code',
    uipath_workflow_analyzer: 'Retrieving workflow analyzer results',
    retrieve_company_guidelines: 'Searching guidelines',
    google_search: 'Searching the web'
  };

  const getToolLabel = (toolName, status) => {
    if (status === 'tool_end') {
      return 'Thinking';
    }
    if (status === 'tool_error') {
      const baseLabel = toolLabelMap[toolName] || toolName;
      return `Failed: ${baseLabel}`;
    }
    const baseLabel = toolLabelMap[toolName] || `Running ${toolName}`;
    return baseLabel;
  };

  const isThinkingStep = (step) => !step?.tool || step.tool === 'thinking';

  const isToolCallStep = (step) => !isThinkingStep(step) || step?.level === 'sub';

  // Load conversations, folders, and models on mount
  useEffect(() => {
    loadConversations();
    loadFoldersAndProjects();
    loadModelSettings();
  }, [user?.id]);

  // Load folders and projects for filters
  const loadFoldersAndProjects = async () => {
    try {
      setIsLoadingFilters(true);
      const response = await folderService.getFoldersAndProjects(user?.id || null);

      const projects = [];
      const collectProjects = (folder, path = '') => {
        const currentPath = path ? `${path} / ${folder.name}` : folder.name;
        if (folder.projects) {
          folder.projects.forEach((project) => {
            projects.push({ ...project, folderPath: currentPath, folderId: folder.id });
          });
        }
        if (folder.children) {
          folder.children.forEach((child) => collectProjects(child, currentPath));
        }
      };

      if (response.data.shared) {
        response.data.shared.forEach((folder) => collectProjects(folder));
      }

      if (response.data.personal) {
        response.data.personal.forEach((folder) => collectProjects(folder, 'Personal'));
      }

      if (response.data.orphanedShared) {
        response.data.orphanedShared.forEach((project) => {
          projects.push({ ...project, folderPath: 'No Folder', folderId: null });
        });
      }

      setFolderTree({
        shared: response.data.shared || [],
        personal: response.data.personal || [],
        orphanedShared: response.data.orphanedShared || [],
      });
      setAllProjects(projects);

      const allProjectIds = projects.map((project) => project.id);
      setSelectedProjectIds((prev) => {
        if (!prev || prev.length === 0) {
          return allProjectIds;
        }
        const validIds = prev.filter((id) => allProjectIds.includes(id));
        return validIds.length > 0 ? validIds : allProjectIds;
      });
    } catch (err) {
      console.error('Error loading folders and projects:', err);
    } finally {
      setIsLoadingFilters(false);
    }
  };

  // Load conversations list
  const loadConversations = async () => {
    try {
      setIsLoadingConversations(true);
      const response = await chatService.getConversations(null, true); // isCrossProject = true
      setConversations(response.data || []);
      
      // Set isLoading to false on initial load if no conversation is selected
      if (!currentConversationId) {
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
      setIsLoading(false); // Also set to false on error
    } finally {
      setIsLoadingConversations(false);
    }
  };

  // Load a specific conversation
  const loadConversation = async (conversationId) => {
    try {
      setIsLoading(true);
      setCurrentConversationId(conversationId);
      const response = await chatService.getConversationMessages(conversationId);
      setMessages(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError('Failed to load conversation');
    } finally {
      setIsLoading(false);
    }
  };

  // Create a new conversation
  const handleNewChat = async () => {
    setCurrentConversationId(null);
    setMessages([]);
    setMetadata(null);
    setError(null);
    setIsLoading(false); // Ensure loading overlay is hidden for new chat
    setSliderValue(2);
    setIsCustomSelection(false);

    // Always use center slider preset defaults for new chats
    if (sliderPresets.length > 0 && availableModels.length > 0) {
      applySliderPreset(2, sliderPresets, availableModels);
    }
  };

  // Delete a conversation
  const handleDeleteConversation = async (conversationId) => {
    try {
      await chatService.deleteConversation(conversationId);
      
      // If we deleted the current conversation, clear it
      if (conversationId === currentConversationId) {
        setCurrentConversationId(null);
        setMessages([]);
      }
      
      // Refresh conversations list
      await loadConversations();
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError('Failed to delete conversation');
    }
  };

  // Load available models and slider presets
  const loadModelSettings = async () => {
    try {
      const [modelsRes, presetsRes] = await Promise.all([
        llmConfigService.getAvailableModels(),
        llmConfigService.getSliderPresets()
      ]);

      const models = modelsRes.data || [];
      const mergedPresets = mergeSliderPresets(models, presetsRes.data || []);

      setAvailableModels(models);
      setSliderPresets(mergedPresets);

      // Ignore stored model preference and always initialize from center slider preset
      applySliderPreset(2, mergedPresets, models);
    } catch (err) {
      console.error('Error loading model settings:', err);
      setCurrentModel({ modelName: 'gemini-2.5-pro', displayName: 'Gemini 2.5 Pro', provider: 'vertex' });
      setCurrentThinkingLevel('low');
      setSliderValue(2);
      setIsCustomSelection(false);
      setSliderPresets([]);
    }
  };

  // Handle model change
  const handleModelChange = (modelName) => {
    const selectedModel = buildModelSelection(availableModels, modelName);
    if (!selectedModel) {
      return;
    }

    setCurrentModel(selectedModel);
    setCurrentThinkingLevel((prev) => {
      const normalized = normalizeThinkingLevel(prev);
      if (normalized === 'minimal' && !isMinimalSupported(selectedModel.modelName)) {
        return 'low';
      }
      return normalized;
    });
    setIsCustomSelection(true);
  };

  const handleThinkingLevelChange = (thinkingLevel) => {
    const normalized = normalizeThinkingLevel(thinkingLevel);
    if (normalized === 'minimal' && !isMinimalSupported(currentModel?.modelName)) {
      setCurrentThinkingLevel('low');
      return;
    }

    setCurrentThinkingLevel(normalized);
    setIsCustomSelection(true);
  };

  const handleSliderChange = (nextSliderValue) => {
    applySliderPreset(nextSliderValue, sliderPresets, availableModels);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Strip markdown formatting from text
  const stripMarkdown = (text) => {
    let cleaned = text;
    
    // Remove bold (**text** or __text__)
    cleaned = cleaned.replace(/\*\*([^*]+?)\*\*/g, '$1');
    cleaned = cleaned.replace(/__([^_]+?)__/g, '$1');
    
    // Remove italic (*text* or _text_)
    cleaned = cleaned.replace(/\*([^*]+?)\*/g, '$1');
    cleaned = cleaned.replace(/_([^_]+?)_/g, '$1');
    
    // Remove code blocks (```code```)
    cleaned = cleaned.replace(/```[\s\S]*?```/g, (match) => {
      return match.replace(/```\w*\n?/g, '').replace(/```/g, '');
    });
    
    // Remove inline code (`code`)
    cleaned = cleaned.replace(/`([^`]+)`/g, '$1');
    
    // Remove headers (# ## ###)
    cleaned = cleaned.replace(/^#{1,6}\s+/gm, '');
    
    // Remove links [text](url)
    cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    
    // Remove images ![alt](url)
    cleaned = cleaned.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1');
    
    return cleaned;
  };

  // Copy message to clipboard
  const handleCopyMessage = async (messageId, content) => {
    try {
      const plainText = stripMarkdown(content);
      await navigator.clipboard.writeText(plainText);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setError('Failed to copy message');
    }
  };

  // Export message to Google Docs
  const handleExportToGoogleDocs = async (messageId, content) => {
    try {
      setExportingMessageId(messageId);
      const response = await chatService.exportMessageToGoogleDocs(messageId, content, 'Cross-Project Analysis');
      
      if (response.data && response.data.documentUrl) {
        const { documentUrl, title } = response.data;
        setExportedDocument({ url: documentUrl, title });
        setShowExportModal(true);
      }
    } catch (err) {
      console.error('Error exporting to Google Docs:', err);
      
      // Check if we need to request Google Docs permissions
      if (err.response?.data?.needsAuth) {
        const currentPath = window.location.pathname;
        const authUrl = `/api/google-docs/auth?returnTo=${encodeURIComponent(currentPath)}`;
        setGoogleDocsAuthUrl(authUrl);
        setShowGoogleDocsPermissionModal(true);
        return;
      }
      
      let errorMessage = 'Failed to export to Google Docs. ';
      if (err.response?.status === 401) {
        errorMessage += 'Please log in with Google to use this feature.';
      } else if (err.response?.data?.error) {
        errorMessage += err.response.data.error;
      }
      alert(errorMessage);
    } finally {
      setExportingMessageId(null);
    }
  };

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const getRecentMessageSnippets = () => {
    const recent = messages.slice(-7);
    return recent.map((msg, idx) => {
      const text = String(msg.message || msg.text || '');
      const isLatestTwo = idx >= recent.length - 2;
      return {
        id: msg.id,
        sender: msg.sender,
        text: isLatestTwo ? text : text.slice(0, 200),
        ...(isLatestTwo && msg.llmExecution ? { llmExecution: msg.llmExecution } : {})
      };
    });
  };

  const buildFeedbackContext = (messageId) => {
    // Find the target message and include its full content + llmExecution
    const targetMsg = messages.find((m) => m.id === messageId);
    const targetContext = targetMsg
      ? {
          targetMessageContent: String(targetMsg.message || targetMsg.text || ''),
          targetMessageSender: targetMsg.sender,
          targetLlmExecution: targetMsg.llmExecution || null
        }
      : {};

    return {
      userId: user?.id || null,
      conversationId: currentConversationId,
      messageId,
      projectIds: selectedProjectIds,
      modelName: currentModel?.modelName || null,
      pageUrl: window.location.href,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      language: navigator.language,
      online: navigator.onLine,
      screen: {
        width: window.screen.width,
        height: window.screen.height
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      recentMessages: getRecentMessageSnippets(),
      lastServerMetadata: metadata || null,
      ...targetContext,
      consoleErrors: getRecentErrors()
    };
  };

  const openFeedbackModal = ({ messageId = null, rating = null, type = 'BUG' }) => {
    setFeedbackTarget({ messageId, rating, initialType: type });
    setShowFeedbackModal(true);
  };

  const handleSubmitFeedback = async (formData) => {
    const payload = {
      ...formData,
      conversationId: currentConversationId,
      messageId: feedbackTarget.messageId,
      projectId: null,
      isCrossProject: true,
      contextData: buildFeedbackContext(feedbackTarget.messageId)
    };

    await feedbackService.submitFeedback(payload);
  };

  const handleRateMessage = async (messageId, rating) => {
    if (ratedMessages[messageId] === rating) {
      return;
    }

    setRatedMessages((prev) => ({
      ...prev,
      [messageId]: rating
    }));

    if (rating < 0) {
      openFeedbackModal({ messageId, rating, type: 'BUG' });
      return;
    }

    try {
      await feedbackService.submitFeedback({
        type: 'PRAISE',
        title: 'Thumbs up',
        description: 'User rated this response as helpful.',
        rating,
        conversationId: currentConversationId,
        messageId,
        projectId: null,
        isCrossProject: true,
        contextData: buildFeedbackContext(messageId)
      });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  const selectedProjects = useMemo(
    () => allProjects.filter((project) => selectedProjectIds.includes(project.id)),
    [allProjects, selectedProjectIds]
  );
  const selectedPreview = selectedProjects.slice(0, 3);

  const sendMessage = async () => {
    const message = inputMessage.trim();
    if (!message || isTyping) return;
    if (selectedProjectIds.length === 0) {
      setError('Select at least one project to run cross-project analysis.');
      return;
    }

    const streamController = new AbortController();
    activeStreamControllerRef.current = streamController;

    // Clear input and error immediately
    setInputMessage('');
    setIsTyping(true);
    setError(null);
    setStreamingStatus(null);
    setThinkingSteps([]);

    // Add user message optimistically
    const tempUserMsg = {
      id: `temp-user-${Date.now()}`,
      message,
      sender: 'user',
      timestamp: new Date().toISOString()
    };
    const tempAssistantId = `temp-assistant-${Date.now()}`;
    const tempAssistantMsg = {
      id: tempAssistantId,
      message: '',
      sender: 'assistant',
      timestamp: new Date().toISOString(),
      isStreaming: true,
      thinkingSteps: []
    };
    setMessages(prev => [...prev, tempUserMsg, tempAssistantMsg]);

    const updateThinkingSteps = (updateFn) => {
      setThinkingSteps(prev => {
        const updated = updateFn(prev);
        setMessages(msgs => msgs.map(m =>
          m.id === tempAssistantId ? { ...m, thinkingSteps: updated } : m
        ));
        return updated;
      });
    };

    try {
      let streamCompleted = false;

      for await (const event of chatService.sendMessageStream(
        null, // no specific projectId for cross-project
        message, // Send the original message without prefix
        currentConversationId,
        true, // isCrossProject = true
        null, // processId
        selectedProjectIds, // Pass selected project IDs as parameter
        currentModel?.modelName || null,
        currentThinkingLevel || null,
        { signal: streamController.signal }
      )) {
        if (event.type === 'status') {
          const label = event.tool ? getToolLabel(event.tool, event.status) : 'Thinking';
          setStreamingStatus({
            tool: event.tool || null,
            status: event.status || 'thinking',
            label,
            detail: event.detail || null
          });

          if (event.status === 'thinking') {
            updateThinkingSteps(prev => {
              const lastStep = prev[prev.length - 1];
              if (lastStep?.tool === 'thinking') {
                return prev;
              }
              const hasRunningThinking = prev.some(step => step.tool === 'thinking' && step.status === 'running');
              if (hasRunningThinking) {
                return prev;
              }
              return [...prev, { tool: 'thinking', label: 'Thinking', status: 'running', startedAt: Date.now() }];
            });
          }

          if (event.status === 'tool_start' && event.tool) {
            const stepLabel = toolLabelMap[event.tool] || event.tool;
            const detail = event.detail || null;
            const fullDetail = event.fullDetail || detail;
            updateThinkingSteps(prev => {
              const now = Date.now();
              const closedThinking = prev.map(step =>
                step.tool === 'thinking' && step.status === 'running'
                  ? { ...step, status: 'done', durationMs: now - step.startedAt }
                  : step
              );
              return [...closedThinking, { tool: event.tool, label: stepLabel, detail, fullDetail, status: 'running', startedAt: now }];
            });
          }

          if (event.status === 'tool_end' && event.tool) {
            updateThinkingSteps(prev => {
              const now = Date.now();
              const updated = prev.map(step =>
                step.tool === event.tool && step.status === 'running'
                  ? { ...step, status: 'done', durationMs: now - step.startedAt }
                  : step
              );
              return updated;
            });
          }

          if (event.status === 'tool_error' && event.tool) {
            updateThinkingSteps(prev => {
              const now = Date.now();
              return prev.map(step =>
                step.tool === event.tool && step.status === 'running'
                  ? {
                      ...step,
                      status: 'failed',
                      durationMs: now - step.startedAt,
                      detail: event.detail || step.detail,
                      fullDetail: event.fullDetail || event.detail || step.fullDetail || step.detail
                    }
                  : step
              );
            });
          }
        }

        if (event.type === 'token') {
          setMessages(prev => prev.map(m =>
            m.id === tempAssistantId
              ? { ...m, message: `${m.message || ''}${event.content || ''}` }
              : m
          ));
        }

        if (event.type === 'done') {
          streamCompleted = true;
          if (!currentConversationId && event.conversationId) {
            setCurrentConversationId(event.conversationId);
            setConversations(prev => [{
              id: event.conversationId,
              projectId: null,
              displayTitle: message.length > 50 ? message.substring(0, 47) + '...' : message,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              isCrossProject: true,
              _count: { messages: 2 }
            }, ...prev]);
          }

          setMessages(prev => {
            const streamingMsg = prev.find(m => m.id === tempAssistantId);
            const rawSteps = streamingMsg?.thinkingSteps || thinkingSteps;
            const now = Date.now();
            const finalSteps = rawSteps.map(step =>
              step.status === 'running'
                ? { ...step, status: 'done', durationMs: now - step.startedAt }
                : step
            );
            const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id && m.id !== tempAssistantId);
            const assistantWithSteps = finalSteps.length > 0
              ? { ...event.assistantMessage, thinkingSteps: finalSteps }
              : event.assistantMessage;
            return [...withoutTemp, event.userMessage, assistantWithSteps];
          });
          setThinkingSteps([]);

          if (event.metadata) {
            setMetadata(event.metadata);
          }

          if (event.conversationId) {
            setConversations(prev =>
              prev.map(c =>
                c.id === event.conversationId
                  ? { ...c, updatedAt: new Date().toISOString(), _count: { messages: (c._count?.messages || 0) + 2 } }
                  : c
              )
            );
          }
        }

        if (event.type === 'error') {
          throw new Error(event.message || 'Streaming error');
        }
      }

      if (!streamCompleted && streamController.signal.aborted) {
        throw new DOMException('Request aborted', 'AbortError');
      }
    } catch (err) {
      if (isAbortLikeError(err)) {
        setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id && m.id !== tempAssistantId));
        setError(null);
        return;
      }

      console.error('Error sending message:', err);
      
      // Determine user-friendly error message
      let userErrorMessage = 'Sorry, I encountered an error. Please try again.';
      
      if (err.response?.status === 503) {
        userErrorMessage = 'The AI service is temporarily unavailable. Please try again in a moment.';
      } else if (err.response?.status === 504) {
        userErrorMessage = 'The request timed out. Please try a simpler question or try again later.';
      } else if (err.response?.status === 500) {
        userErrorMessage = 'An internal server error occurred. Please try again later.';
      } else if (err.response?.data?.error) {
        userErrorMessage = err.response.data.error;
      } else if (err.message?.includes('Network Error')) {
        userErrorMessage = 'Network connection lost. Please check your internet connection and try again.';
      }
      
      // Show error message in chat
      const errorMsg = {
        id: `error-${Date.now()}`,
        message: `❌ **Error**: ${userErrorMessage}`,
        sender: 'assistant',
        timestamp: new Date().toISOString(),
        isError: true
      };
      
      // Remove temp message and add error
      setMessages(prev => {
        const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id && m.id !== tempAssistantId);
        return [...withoutTemp, errorMsg];
      });
      
      setError(userErrorMessage);
    } finally {
      if (activeStreamControllerRef.current === streamController) {
        activeStreamControllerRef.current = null;
      }
      setIsTyping(false);
      setStreamingStatus(null);
      setThinkingSteps([]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Custom components for ReactMarkdown
  const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      
      return !inline && language ? (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={language}
          PreTag="div"
          className="rounded-lg my-2"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  };

  const suggestedQuestions = [
    "What projects interact with web applications?",
    "Give me a list of Document Understanding implementations",
    "Show me all projects using Excel activities",
    "What workflows could be shared between these projects?"
  ];

  return (
    <div className="flex h-full gap-4">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col rounded-none overflow-hidden px-4 pt-4 pb-4 relative" style={{borderRight: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-black)'}}>
        
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-10" style={{background: 'rgba(34,34,34,0.85)'}}>
            <div style={{color: 'rgba(255,255,255,0.5)'}}>Loading...</div>
          </div>
        )}
        
        {/* Filters Summary */}
        <div className="mb-4 rounded-lg shadow-sm" style={{background: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)', borderTop: '1px solid var(--tqa-green)'}}>
          <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold" style={{color: 'rgba(255,255,255,0.85)'}}>
                <svg className="w-4 h-4" style={{color: 'var(--tqa-green)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Projects included
              </div>
              <p className="text-sm mt-1" style={{color: 'rgba(255,255,255,0.65)'}}>
                {selectedProjectIds.length} of {allProjects.length} projects selected
              </p>
              {selectedProjectIds.length === 0 ? (
                <p className="text-xs mt-1" style={{color: 'var(--tqa-red)'}}>Select at least one project to run cross-project analysis.</p>
              ) : (
                <p className="text-xs mt-1" style={{color: 'rgba(255,255,255,0.5)'}}>
                  {selectedProjectIds.length === allProjects.length
                    ? 'All projects included.'
                    : `Includes ${selectedPreview.map((project) => project.name).join(', ')}${selectedProjects.length > selectedPreview.length ? ' and more.' : '.'}`}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowFiltersModal(true)}
              className="btn-secondary text-xs px-4 py-2"
              disabled={isLoadingFilters}
            >
              Select folders or projects
            </button>
          </div>
        </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm" style={{background: 'rgba(230,100,97,0.1)', border: '1px solid rgba(230,100,97,0.35)', color: 'rgba(230,100,97,0.9)'}}>
              {error}
            </div>
          )}

          {/* Scrollable Chat Messages Area */}
          <div ref={chatContainerRef} className="flex-1 overflow-y-auto mb-4">
            <div className="flex flex-col space-y-3 px-2">
              {messages.length === 0 ? (
                <div className="flex-1" />
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                  <div
                    className={`max-w-[85%] rounded-lg px-4 py-3`}
                    style={
                        msg.sender === 'user'
                          ? {background: 'rgba(130,255,207,0.15)', border: '1px solid rgba(130,255,207,0.35)', color: 'rgba(255,255,255,0.9)'}
                          : msg.isError
                          ? {background: 'rgba(230,100,97,0.1)', border: '1px solid rgba(230,100,97,0.35)', color: 'rgba(230,100,97,0.85)'}
                          : {background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}
                      }
                    >
                    {(msg.sender === 'agent' || msg.sender === 'assistant') ? (
                      <div className="chat-markdown">
                          <ThoughtChain 
                            steps={msg.thinkingSteps} 
                            isStreaming={msg.isStreaming}
                            isToolCallStep={isToolCallStep}
                            isThinkingStep={isThinkingStep}
                          />
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]}
                          components={markdownComponents}
                        >
                          {msg.message}
                        </ReactMarkdown>
                        
                        {/* Display LLM Execution Metrics and Action Buttons */}
                        {msg.llmExecution && (
                          <div className="mt-3 pt-3" style={{borderTop: '1px solid var(--tqa-border-dark)'}}>
                            <div className="flex items-center justify-between gap-4">
                              {/* Left side: Metrics */}
                              <div className="flex items-center gap-3 text-xs" style={{color: 'rgba(255,255,255,0.55)'}}>
                                {msg.timestamp && (
                                  <span style={{color: 'rgba(255,255,255,0.4)'}}>
                                    {formatTimestamp(msg.timestamp)}
                                  </span>
                                )}
                                <span style={{color: 'rgba(255,255,255,0.65)'}}>
                                  🤖 {msg.llmExecution.initialAnalysisModelDisplayName || msg.llmExecution.initialAnalysisModelName || msg.llmExecution.displayName || msg.llmExecution.modelName || 'Unknown model'} ({String(msg.llmExecution.thinkingLevel || 'low').toLowerCase()})
                                </span>
                                <span style={{color: 'rgba(130,255,207,0.8)'}}>
                                  ⏱️ {(msg.llmExecution.durationMs / 1000).toFixed(2)}s
                                </span>
                                <span className="font-medium" style={{color: 'var(--tqa-green)'}}>
                                  💰 ${parseFloat(msg.llmExecution.estimatedCost).toFixed(3)}
                                  {(() => {
                                    // Calculate cumulative cost up to and including this message
                                    const messageIndex = messages.findIndex(m => m.id === msg.id);
                                    const cumulativeCost = messages
                                      .slice(0, messageIndex + 1)
                                      .filter(m => m.llmExecution && m.llmExecution.estimatedCost)
                                      .reduce((sum, m) => sum + parseFloat(m.llmExecution.estimatedCost), 0);
                                    return ` (total: $${cumulativeCost.toFixed(3)})`;
                                  })()}
                                </span>
                              </div>
                              
                              {/* Right side: Action buttons */}
                              {!msg.isStreaming && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleCopyMessage(msg.id, msg.message)}
                                  className="transition-colors p-1.5 rounded"
                                  style={{color: 'rgba(255,255,255,0.4)'}}
                                  onMouseEnter={e => e.currentTarget.style.color='var(--tqa-green)'}
                                  onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.4)'}
                                  title={copiedMessageId === msg.id ? 'Copied!' : 'Copy message'}
                                >
                                  {copiedMessageId === msg.id ? (
                                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleExportToGoogleDocs(msg.id, msg.message)}
                                  disabled={exportingMessageId === msg.id}
                                  className="transition-colors p-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                  style={{color: 'rgba(255,255,255,0.4)'}}
                                  onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.color='rgba(255,255,255,0.75)'; }}
                                  onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.4)'}
                                  title="Export to Google Docs"
                                >
                                  {exportingMessageId === msg.id ? (
                                    <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{borderColor: 'var(--tqa-green)', borderTopColor: 'transparent'}}></div>
                                  ) : (
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#4285F4"/>
                                      <path d="M14 2v6h6" fill="#A1C2FA"/>
                                      <path d="M7 13h10v1H7zm0 2h10v1H7zm0 2h7v1H7z" fill="white"/>
                                    </svg>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleRateMessage(msg.id, 1)}
                                  className={`transition-colors p-1.5 rounded ${
                                    ratedMessages[msg.id] === 1
                                      ? 'text-emerald-400'
                                      : 'hover:text-emerald-400 transition-colors'
                                  }`}
                                  title="Thumbs up"
                                >
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 10c0-.6.4-1 1-1h3v9H3c-.6 0-1-.4-1-1v-7zm5-1.1l3.4-5.2c.2-.4.6-.7 1-.7h.6c1 0 1.7.9 1.5 1.9l-.6 3h4.4c1.2 0 2.1 1.1 1.9 2.2l-1.4 6.3c-.2.9-1 1.5-1.9 1.5H7V8.9z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleRateMessage(msg.id, -1)}
                                  className={`transition-colors p-1.5 rounded ${
                                    ratedMessages[msg.id] === -1
                                      ? 'text-red-400'
                                      : 'hover:text-red-400 transition-colors'
                                  }`}
                                  title="Thumbs down"
                                >
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 3c0-.6.4-1 1-1h3v9H3c-.6 0-1-.4-1-1V3zm5 8.1l3.4 5.2c.2.4.6.7 1 .7h.6c1 0 1.7-.9 1.5-1.9l-.6-3h4.4c1.2 0 2.1-1.1 1.9-2.2l-1.4-6.3c-.2-.9-1-1.5-1.9-1.5H7v9.1z" />
                                  </svg>
                                </button>
                              </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Action Icons for messages without metrics */}
                        {!msg.llmExecution && !msg.isStreaming && (
                          <div className="mt-3 pt-2 flex items-center justify-end gap-2" style={{borderTop: '1px solid var(--tqa-border-dark)'}}>
                            <button
                              onClick={() => handleCopyMessage(msg.id, msg.message)}
                              className="transition-colors p-1.5 rounded"
                              style={{color: 'rgba(255,255,255,0.4)'}}
                              onMouseEnter={e => e.currentTarget.style.color='var(--tqa-green)'}
                              onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.4)'}
                              title={copiedMessageId === msg.id ? 'Copied!' : 'Copy message'}
                            >
                              {copiedMessageId === msg.id ? (
                                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => handleExportToGoogleDocs(msg.id, msg.message)}
                              disabled={exportingMessageId === msg.id}
                              className="transition-colors p-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{color: 'rgba(255,255,255,0.4)'}}
                              onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.color='rgba(255,255,255,0.75)'; }}
                              onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.4)'}
                              title="Export to Google Docs"
                            >
                              {exportingMessageId === msg.id ? (
                                <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{borderColor: 'var(--tqa-green)', borderTopColor: 'transparent'}}></div>
                              ) : (
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#4285F4"/>
                                  <path d="M14 2v6h6" fill="#A1C2FA"/>
                                  <path d="M7 13h10v1H7zm0 2h10v1H7zm0 2h7v1H7z" fill="white"/>
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => handleRateMessage(msg.id, 1)}
                              className={`transition-colors p-1.5 rounded ${
                                ratedMessages[msg.id] === 1
                                  ? 'text-emerald-400'
                                  : 'hover:text-emerald-400 transition-colors'
                              }`}
                              title="Thumbs up"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 10c0-.6.4-1 1-1h3v9H3c-.6 0-1-.4-1-1v-7zm5-1.1l3.4-5.2c.2-.4.6-.7 1-.7h.6c1 0 1.7.9 1.5 1.9l-.6 3h4.4c1.2 0 2.1 1.1 1.9 2.2l-1.4 6.3c-.2.9-1 1.5-1.9 1.5H7V8.9z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleRateMessage(msg.id, -1)}
                              className={`transition-colors p-1.5 rounded ${
                                ratedMessages[msg.id] === -1
                                  ? 'text-red-400'
                                  : 'hover:text-red-400 transition-colors'
                              }`}
                              title="Thumbs down"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 3c0-.6.4-1 1-1h3v9H3c-.6 0-1-.4-1-1V3zm5 8.1l3.4 5.2c.2.4.6.7 1 .7h.6c1 0 1.7-.9 1.5-1.9l-.6-3h4.4c1.2 0 2.1-1.1 1.9-2.2l-1.4-6.3c-.2-.9-1-1.5-1.9-1.5H7v9.1z" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      msg.message
                    )}
                    {msg.timestamp && msg.sender === 'user' && (
                      <div className="text-xs mt-2" style={{color: 'rgba(255,255,255,0.45)'}}>
                        {formatTimestamp(msg.timestamp)}
                      </div>
                    )}
                  </div>
                  </div>
                ))
              )}
            
          </div>
        </div>
        
        {/* Fixed Bottom Section */}
        <div className="flex-shrink-0">
            {messages.length === 0 && (
              <div className="mb-4 px-2">
                <div className="max-w-2xl text-center mx-auto">
                  <p className="font-medium mb-2" style={{color: 'rgba(255,255,255,0.75)'}}>
                    Cross-Project Chat
                  </p>
                  <p className="text-sm mb-4" style={{color: 'rgba(255,255,255,0.5)'}}>
                    Ask questions that span multiple projects or analyze patterns across your workspace
                  </p>
                  <div className="mb-2">
                    <p className="text-sm mb-3" style={{color: 'rgba(255,255,255,0.55)'}}>Try asking:</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {suggestedQuestions.map((question, idx) => (
                        <button
                          key={idx}
                          onClick={() => setInputMessage(question)}
                          className="text-xs px-3 py-1.5 rounded-full transition-colors"
                          style={{border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.65)', background: 'var(--tqa-surface-2)'}}
                          onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(130,255,207,0.4)'; e.currentTarget.style.color='var(--tqa-green)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor='var(--tqa-border-dark)'; e.currentTarget.style.color='rgba(255,255,255,0.65)'; }}
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* AI Disclaimer + Feedback */}
            <div className="mb-4 px-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs italic" style={{color: 'rgba(255,255,255,0.4)'}}>
                ⚠️ Responses are AI-generated and may contain errors. Use as reference only.
              </p>
              <button
                type="button"
                onClick={() => openFeedbackModal({ type: 'IDEA' })}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full transition-colors"
                style={{color: 'rgba(255,255,255,0.5)', border: '1px solid var(--tqa-border-dark)'}}
                onMouseEnter={e => { e.currentTarget.style.color='var(--tqa-green)'; e.currentTarget.style.borderColor='rgba(130,255,207,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.color='rgba(255,255,255,0.5)'; e.currentTarget.style.borderColor='var(--tqa-border-dark)'; }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20l9-8-9-8-9 8 9 8z" />
                </svg>
                Feedback
              </button>
            </div>

            {/* Input Box */}
            <div className="px-2">
              <div className="flex space-x-2">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={
                    selectedProjectIds.length > 0
                      ? `Ask about ${selectedProjectIds.length} selected project${selectedProjectIds.length > 1 ? 's' : ''}... (Ctrl+Enter for new line)`
                      : 'Select projects to start a cross-project analysis...'
                  }
                  className="flex-grow rounded-lg px-4 py-2 focus:outline-none resize-none min-h-[42px] max-h-[200px]"
                  rows={1}
                  style={{
                    border: '1px solid var(--tqa-border-dark)',
                    background: 'var(--tqa-surface-2)',
                    color: 'rgba(255,255,255,0.85)',
                    height: 'auto',
                    overflow: 'auto'
                  }}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                  }}
                  disabled={isTyping}
                />
                <button
                  onClick={isTyping ? stopActiveResponse : sendMessage}
                  disabled={isTyping ? false : (!inputMessage.trim() || selectedProjectIds.length === 0)}
                  className={`px-4 py-2 self-end ${
                    isTyping
                      ? 'bg-red-600 text-white rounded-md hover:bg-red-700'
                      : 'btn-primary disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                >
                  {isTyping ? 'Stop' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>

      {/* Chat History Sidebar */}
      <div className="w-80 h-full">
        <ChatHistorySidebar
          conversations={conversations}
          currentConversationId={currentConversationId}
          onSelectConversation={loadConversation}
          onNewChat={handleNewChat}
          onDeleteConversation={handleDeleteConversation}
          onAnalyzeCode={null} // Not applicable for cross-project
          isLoading={isLoadingConversations}
          availableModels={availableModels}
          currentModel={currentModel}
          currentThinkingLevel={currentThinkingLevel}
          sliderValue={sliderValue}
          isCustomSelection={isCustomSelection}
          onModelChange={handleModelChange}
          onThinkingLevelChange={handleThinkingLevelChange}
          onSliderChange={handleSliderChange}
        />
      </div>

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleSubmitFeedback}
        initialType={feedbackTarget.initialType}
        initialRating={feedbackTarget.rating}
      />

      <CrossProjectFiltersModal
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        onApply={(projectIds) => {
          setSelectedProjectIds(projectIds);
          if (projectIds.length > 0) {
            setError(null);
          }
          setShowFiltersModal(false);
        }}
        folderTree={folderTree}
        initialSelectedProjectIds={selectedProjectIds}
        isLoading={isLoadingFilters}
      />
    </div>
  );
};

export default CrossProjectTab;